import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import AddButton from '../../components/AddButton';
import ActionToolbar from '../../components/ActionToolbar';
import DataList from '../../components/DataList';
import EmptyTableIndicator from '../../components/EmptyTableIndicator';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ConstitutionModal from './ConstitutionModal';
import { getConstitutionsTableConfig, PAGE_CONFIG } from './config';
import { useTheme } from '../../context/ThemeProvider';

// Import custom hooks
import { useModals } from '../../hooks/useModals';
import {
    useCreateConstitution,
    useUpdateConstitution,
    useDeleteConstitution,
    useConstitutionsData,
    useConstitutionForm,
    useConstitutionActions
} from './hooks/index';

const Constitutions = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`constitutions:${key}`), [t]);
    const { theme } = useTheme();
    const queryClient = useQueryClient();
     // Modal management hook
     const {
        isOpen,
        isDeleteOpen,
        selectedItem,
        handleOpenModal,
        handleCloseModal,
        handleOpenDeleteModal,
        handleCloseDeleteModal
    } = useModals();

    // CRUD hooks
    const { createConstitution, createStatus } = useCreateConstitution(queryClient, theme);
    const { updateConstitution, updateStatus } = useUpdateConstitution(queryClient, theme);
    const { deleteConstitution, isDeleting } = useDeleteConstitution(queryClient, theme, handleCloseDeleteModal);

    // Data management hook
    const {
        data,
        pagination,
        isLoading,
        searchQuery,
        handlePageChange,
        handlePageSizeChange,
        handleSearch
    } = useConstitutionsData();

   

    // Form management hook
    const {
        form,
        files,
        errors,
        isEdit,
        selectedItem: formSelectedItem,
        handleInputChange,
        handleFileChange,
        resetForm,
        setEditData,
        handleSubmit
    } = useConstitutionForm(createConstitution, updateConstitution, handleCloseModal);

   

    // Actions hook
    const {
        downloadingId,
        handleEdit,
        handleDownload,
        truncateDescription
    } = useConstitutionActions(theme, handleOpenDeleteModal, setEditData);

  

    // Enhanced handlers that combine modal and form logic
    const handleOpenCreateModal = useCallback(() => {
        handleOpenModal();
        resetForm();
    }, [handleOpenModal, resetForm]);

    const handleCloseModalWithReset = useCallback(() => {
        handleCloseModal();
        resetForm();
    }, [handleCloseModal, resetForm]);

    const handleEditWithModal = useCallback((item) => {
        handleOpenModal();
        handleEdit(item);
    }, [handleOpenModal, handleEdit]);

    const handleDeleteWithModal = useCallback((item) => {
        handleOpenDeleteModal(item);
    }, [handleOpenDeleteModal]);

    // Table configuration
    const columns = useMemo(() => 
        getConstitutionsTableConfig(truncateDescription, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId), 
        [truncateDescription, handleEditWithModal, handleDeleteWithModal, handleDownload, downloadingId]
    );

    return (
        <>
            <div className="flex">
                <div className="flex-1 p-4">
                    <PageHeader items={PAGE_CONFIG.BREADCRUMB} pageTitle={PAGE_CONFIG.PAGE_TITLE} />
                    <ActionToolbar 
                        searchQuery={searchQuery} 
                        onSearchChange={handleSearch} 
                        actionButton={
                            <AddButton 
                                isLoading={false} 
                                onClick={handleOpenCreateModal} 
                                label={tr('create')} 
                            />
                        } 
                        disabled={false} 
                    />
                    {data?.length > 0 ? (
                        <DataList 
                            data={data} 
                            columns={columns} 
                            pagination={pagination} 
                            onPageChange={handlePageChange} 
                            onPageSizeChange={handlePageSizeChange} 
                        />
                    ) : (
                        <EmptyTableIndicator 
                            colSpan={5} 
                            isLoading={isLoading} 
                            icon={faSearch} 
                            description={tr('searchDescription')} 
                            placeholder={tr('searchPlaceholder')} 
                            loadingMessage={tr('searchLoadingMessage')} 
                        />
                    )}
                </div>
            </div>

            <ConstitutionModal
                handleChange={handleInputChange}
                handleFileChange={handleFileChange}
                handleSubmit={handleSubmit}
                form={form}
                files={files}
                isOpen={isOpen}
                onClose={handleCloseModalWithReset}
                isEdit={isEdit}
                selectedItem={formSelectedItem}
                setIsEdit={setEditData}
                onSubmit={handleSubmit}
                status={createStatus}
                isLoading={createStatus === 'pending' || updateStatus === 'pending'}
                errors={errors}
            />

            <ConfirmationDialog
                title={`Delete Constitution`}
                onConfirm={() => deleteConstitution(selectedItem?.id)}
                type="DELETE"
                message={`Are you sure you want to delete ${selectedItem?.title?.length > 50 ? selectedItem?.title?.substring(0, 50) + '...' : selectedItem?.title}?`}
                isOpen={isDeleteOpen}
                onClose={handleCloseDeleteModal}
                isLoading={isDeleting}
            />
        </>
    );
};

export default Constitutions; 